import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Zap } from "lucide-react";
import { useLeadModal } from "./LeadModalContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USE_CASES = [
  { value: "", label: "Select use case…" },
  { value: "event_activation", label: "Event activation / pop-up" },
  { value: "luxury_hotel", label: "Luxury hotel" },
  { value: "fine_dining", label: "Fine dining / F&B" },
  { value: "retail_flagship", label: "Retail flagship" },
  { value: "corporate_event", label: "Corporate event" },
  { value: "wellness_spa", label: "Wellness / spa" },
  { value: "museum_culture", label: "Museum / cultural space" },
  { value: "other", label: "Other" },
];

const TRAFFIC_BUCKETS = [
  { value: "", label: "Monthly foot traffic…" },
  { value: "<500", label: "< 500 / month" },
  { value: "500-2k", label: "500 – 2,000 / month" },
  { value: "2k-10k", label: "2,000 – 10,000 / month" },
  { value: "10k-50k", label: "10,000 – 50,000 / month" },
  { value: "50k+", label: "50,000+ / month" },
  { value: "unsure", label: "Not sure yet" },
];

const COPY = {
  demo_request: {
    eyebrow: "BOOK A DEMO",
    title: "See Stori running on a real installation.",
    lead: "20 minutes. Live dashboards. The same ROI calculator that closed our last 12 deals.",
    submitLabel: "Book my demo",
    success: "Demo booked. Watch your inbox — we'll be in touch within 1 business day.",
    endpoint: "/leads/demo-request",
  },
  trial_signup: {
    eyebrow: "FREE TRIAL · 7 DAYS",
    title: "Start your free 7-day trial.",
    lead: "No credit card required. Cancel anytime. Full access to playbooks, analytics, and CRM sync.",
    submitLabel: "Start my free trial",
    success: "You're in. Check your email — your trial workspace is on its way.",
    endpoint: "/leads/trial-signup",
  },
};

const initialForm = {
  name: "",
  email: "",
  company: "",
  role: "",
  use_case: "",
  monthly_traffic: "",
  consent: true,
};

export default function LeadFormModal() {
  const { open, mode, closeModal } = useLeadModal();
  const copy = COPY[mode] || COPY.demo_request;
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // Reset form whenever modal re-opens or mode changes
  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open, mode]);

  // Body scroll-lock + Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && closeModal();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeModal]);

  const canSubmit = useMemo(
    () =>
      form.name.trim() &&
      form.email.trim() &&
      form.company.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
      form.consent &&
      status !== "submitting",
    [form, status]
  );

  const update = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}${copy.endpoint}`, form, { timeout: 12000 });
      setStatus("success");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(typeof detail === "string" ? detail : "Please check your details and try again.");
      setStatus("error");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lead-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-modal-title"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={closeModal}
    >
      <button
        aria-label="Close"
        data-testid="lead-modal-close"
        onClick={closeModal}
        className="absolute top-6 right-6 w-11 h-11 rounded-full border border-stori-border text-white hover:border-stori-cyan hover:text-stori-cyan transition-colors flex items-center justify-center"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="w-full max-w-xl my-12 rounded-2xl border border-stori-border bg-stori-surface1 p-7 lg:p-9 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-stori-cyan/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative">
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2">
            <span className="inline-block h-px w-8 bg-stori-cyan" />
            {copy.eyebrow}
          </div>
          <h2
            id="lead-modal-title"
            className="mt-3 font-heading text-3xl lg:text-4xl font-semibold text-white tracking-tight leading-tight"
          >
            {copy.title}
          </h2>
          <p className="mt-3 text-stori-muted leading-relaxed text-sm lg:text-base">
            {copy.lead}
          </p>

          {/* Speed promise — bold, prominent on every variant */}
          <div className="mt-5 flex items-start gap-3 px-4 py-3 rounded-lg border border-stori-cyan/40 bg-stori-cyan/5">
            <Zap className="w-5 h-5 text-stori-cyan flex-shrink-0 mt-0.5" fill="currentColor" />
            <p className="font-heading text-base lg:text-lg font-bold text-white leading-snug">
              We can be live at your venue in <span className="text-stori-cyan cyan-text-glow">under 10 minutes.</span>
            </p>
          </div>

          {status === "success" ? (
            <div
              data-testid="lead-modal-success"
              className="mt-8 p-6 rounded-xl border border-stori-cyan/40 bg-stori-cyan/5 cyan-glow"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-stori-cyan flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    {copy.success}
                  </h3>
                  <p className="mt-2 text-sm text-stori-muted leading-relaxed">
                    Lead reference: a copy of your details is on its way to{" "}
                    <span className="text-white font-mono">{form.email}</span>.
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                data-testid="lead-modal-success-close"
                className="mt-6 w-full bg-stori-cyan text-black font-medium px-5 py-3 rounded-md hover:bg-stori-cyanHover transition-colors"
              >
                Got it
              </button>
            </div>
          ) : (
            <form className="mt-7 space-y-4" onSubmit={onSubmit} data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Full name"
                  required
                  testId="field-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Daniel Hahn"
                  autoComplete="name"
                />
                <Field
                  label="Work email"
                  type="email"
                  required
                  testId="field-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="daniel@aurelia.co"
                  autoComplete="email"
                />
              </div>
              <Field
                label="Company"
                required
                testId="field-company"
                value={form.company}
                onChange={update("company")}
                placeholder="Aurelia Hotel Group"
                autoComplete="organization"
              />
              <Field
                label="Your role"
                testId="field-role"
                value={form.role}
                onChange={update("role")}
                placeholder="GM · Marketing Director · Event Lead"
                autoComplete="organization-title"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <SelectField
                  label="Use case"
                  testId="field-use-case"
                  value={form.use_case}
                  onChange={update("use_case")}
                  options={USE_CASES}
                />
                <SelectField
                  label="Monthly traffic"
                  testId="field-monthly-traffic"
                  value={form.monthly_traffic}
                  onChange={update("monthly_traffic")}
                  options={TRAFFIC_BUCKETS}
                />
              </div>

              <label className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={update("consent")}
                  data-testid="field-consent"
                  className="mt-0.5 accent-stori-cyan w-4 h-4"
                />
                <span className="text-xs text-stori-muted leading-relaxed">
                  I agree to receive a follow-up from the Stori team. We don&apos;t share or sell
                  your data — GDPR + CCPA compliant.
                </span>
              </label>

              {status === "error" && (
                <div
                  data-testid="lead-modal-error"
                  className="flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="lead-modal-submit"
                className={`group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-medium transition-all ${
                  canSubmit
                    ? "bg-stori-cyan text-black hover:bg-stori-cyanHover hover:translate-y-[-1px]"
                    : "bg-stori-surface2 text-stori-muted cursor-not-allowed"
                }`}
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    {copy.submitLabel}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-stori-muted pt-1">
                · no credit card · response within 1 business day ·
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, required, type = "text", value, onChange, placeholder, autoComplete }) {
  return (
    <label className="block">
      <span className="block label-eyebrow mb-2">
        {label} {required && <span className="text-stori-cyan">*</span>}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testId}
        className="w-full bg-stori-bg border border-stori-border focus:border-stori-cyan focus:outline-none focus:ring-1 focus:ring-stori-cyan/40 px-4 py-3 rounded-md text-white placeholder-stori-muted/60 text-sm transition-colors"
      />
    </label>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block label-eyebrow mb-2">{label}</span>
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-stori-bg border border-stori-border focus:border-stori-cyan focus:outline-none focus:ring-1 focus:ring-stori-cyan/40 px-4 py-3 rounded-md text-white text-sm transition-colors appearance-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-stori-bg text-white">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
